import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { AnalyticsService } from './analytics.service';
import { APP_EVENTS } from './analytics.events';

/**
 * Reports uncaught front-end errors alongside the product funnel.
 *
 * WHY THIS IS WORTH HAVING
 * ────────────────────────────────────────────────────────────────────────
 * A JavaScript error that breaks a page is invisible in every funnel we have.
 * A client whose brief screen crashed looks exactly like a client who lost
 * interest — both simply stop appearing. So a bug reads as a product problem,
 * and the fix goes to the wrong team.
 *
 * With this, an abandonment spike can be checked against an error spike on the
 * same screen, and the two can be told apart.
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 * ────────────────────────────────────────────────────────────────────────
 * It is NOT error monitoring. It records that an error happened, where, and
 * its type — not a full stack trace. Stack traces can contain user data and
 * would blow past Mixpanel's property limits; a real tool (Sentry) is the
 * right home for them. This exists so the funnel can be read honestly.
 *
 * It also STILL LOGS TO THE CONSOLE. Replacing Angular's default handler
 * without re-logging would hide every error from developers — trading a
 * visible problem for an invisible one.
 */
@Injectable()
export class AnalyticsErrorHandler implements ErrorHandler {
  /** Errors already reported this page-load, so one broken render loop
   *  cannot emit thousands of identical events. */
  private reported = new Set<string>();

  /** Hard ceiling per page load. A component erroring on every change
   *  detection cycle would otherwise flood both the tool and the user's
   *  network connection. */
  private static readonly MAX_PER_PAGE = 10;
  private count = 0;

  // Injector, not AnalyticsService directly: ErrorHandler is constructed very
  // early in Angular's bootstrap, before most providers exist. Injecting the
  // service eagerly creates a circular dependency that fails at startup.
  constructor(private injector: Injector) {}

  handleError(error: any): void {
    // ALWAYS log first. If anything below throws, the developer still sees it.
    // eslint-disable-next-line no-console
    console.error(error);

    try {
      if (this.count >= AnalyticsErrorHandler.MAX_PER_PAGE) return;

      const message = String(error?.message ?? error ?? 'Unknown error').slice(0, 300);
      const name = String(error?.name ?? 'Error').slice(0, 80);
      const path = (typeof window !== 'undefined' && window.location?.pathname) || '';

      // De-duplicate on message + path: the same failure on the same screen is
      // one problem, however many times it repeats.
      const key = `${name}|${message}|${path}`;
      if (this.reported.has(key)) return;
      this.reported.add(key);
      this.count += 1;

      this.injector.get(AnalyticsService).track(APP_EVENTS.APP_ERROR, {
        error_name: name,
        error_message: message,
        path,
        // Chunk-load failures mean a stale tab against a new deploy, not a
        // code defect. Worth separating — they need a different response.
        is_chunk_load_error: /ChunkLoadError|Loading chunk/i.test(message),
      });
    } catch {
      // An error handler that throws would replace the original error with
      // its own and lose the real one.
    }
  }
}
